import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./registerScreen.module.css";
import { Box, Button, FormControl, TextField, Typography, Link, RadioGroup, FormControlLabel, Radio, FormLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSubscriptionTariffsRequests } from "../../api/subscription_tariffs";
import { SubscriptionTariff } from "../../types/subscriptions";
import { useTenantsRequests } from "../../api/tenants";
import { useSnackbar } from "notistack";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../consts";

export const RegisterScreen = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { query } = useSubscriptionTariffsRequests();
  const { loading, create, error } = useTenantsRequests();

  const [registerData, setRegisterData] = useState({
    email: "",
    tenantName: "",
    subscriptionTariff: "",
  });
  const [subscriptionTariffs, setSubscriptionTariffs] = useState<SubscriptionTariff[]>([]);
  const [errors, setErrors] = useState({
    subscriptionTariff: "",
  });

  useEffect(() => {
    query().then((data) => { setSubscriptionTariffs(data?.results || []); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegisterData((prev) => ({ ...prev, [name]: value }));
    if (name === "subscriptionTariff" && value) {
      setErrors((prev) => ({ ...prev, subscriptionTariff: "" }));
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { success } = await create({ name: registerData.tenantName, email: registerData.email, subscription_tariff: registerData.subscriptionTariff });
    if (success) {
      navigate('/login');
      enqueueSnackbar(t("Registration successful. Please login to continue."), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(t("Registration failed. Please try again."), ERROR_SNACKBAR);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.registerBox}>
        <form onSubmit={handleRegisterSubmit}>
          <FormControl variant="standard" className={styles.formControl} fullWidth>
            <div className={styles.headerTitle}>{t("Register")}</div>
            <Typography variant="body2" className={styles.description}>
              {t("Create your account to get started")}
            </Typography>

            <Box className={styles.inputRow}>
              <TextField
                name="email"
                label={t("Email address")}
                type="email"
                size="small"
                sx={{ width: '100%' }}
                value={registerData.email}
                onChange={handleRegisterChange}
                required
              />
              {error && (
                <div className={styles.errorMessage}>{error?.email?.[0].message}</div>
              )}
            </Box>

            <Box className={styles.inputRow}>
              <TextField
                name="tenantName"
                label={t("Workspace name")}
                size="small"
                sx={{ width: '100%' }}
                value={registerData.tenantName}
                onChange={handleRegisterChange}
                required
              />
              {error && (
                <div className={styles.errorMessage}>{error?.name?.[0].message}</div>
              )}
            </Box>

            <Box className={`${styles.inputRow} ${styles.radioRow}`}>
              <FormLabel
                id="subscription-tariff-radio-group"
                error={!!errors.subscriptionTariff}
                className={styles.radioLabel}
              >
                {t("Subscription tariff")}
              </FormLabel>
              <RadioGroup
                aria-labelledby="subscription-tariff-radio-group"
                aria-describedby={errors.subscriptionTariff ? "subscription-tariff-helper-text" : undefined}
                name="subscriptionTariff"
                value={registerData.subscriptionTariff}
                onChange={handleRegisterChange}
                className={styles.radioGroup}
              >
                {subscriptionTariffs?.map((tariff) => (
                  <FormControlLabel key={tariff.id} value={tariff.id} control={<Radio />} label={tariff.name} />
                ))}
              </RadioGroup>
              {error && (
                <div className={styles.errorMessage}>{error?.subscription_tariff?.[0].message}</div>
              )}
            </Box>

            <Button
              variant="contained"
              color="success"
              type="submit"
              fullWidth
              loading={loading}
              className={styles.submitButton}
            >
              {t("Register")}
            </Button>

            <Box className={styles.footerBox}>
              <Typography variant="body2">
                {t("Already have an account?")}{" "}
                <Link href="#" onClick={(e) => { e.preventDefault(); handleBackToLogin(); }}>
                  {t("Log in")}
                </Link>
              </Typography>
            </Box>
          </FormControl>
        </form>
      </Box>
    </Box>
  );
};
